import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigationType, useParams } from 'react-router';
import {
  ADD_FAVORITE_PROFESSIONAL,
  BLOCK_RECENT_PROFESSIONAL,
  changeHubFilter,
  FETCH_FAVORITE_PROFESSIONALS,
  FETCH_RECENT_PROFESSIONALS,
  FETCH_RECOMMENDED_PROFESSIONALS_ACTION,
  getAllPaymnets
} from '../../actions';
import { clearSuccess } from '../../actions/success';
import { trackEvent } from '../../api/analytics';
import { createSuccessSelector } from '../../api/selectors';
import { CardSpinner, Tab, TabList, TabPanel, Tabs } from '../../commonComponents';
import history from '../../history';
import { useLoading } from '../../hooks';
import { useAppSelector } from '../../hooks/useAppSelector';
import { useUserSelector } from '../../hooks/useUserSelector';
import ChevronRight from '../../images/arrow_right.svg';
import MailSvg from '../../images/mail.svg';
import { Colors } from '../../themes/colors';
import { PaymentWarningBanner } from '../PaymentWarningBanner';
import ConfirmPopup from '../shared/ConfirmPopup';
import { FavoritesList } from './components/FavoritesList/FavoritesList';
import { useFavoritesList } from './components/FavoritesList/hooks/useFavoritesList';
import { FilterSelect } from './components/FilterSelect/FilterSelect';
import { useRecentProfessionalsList } from './components/RecentProfessionalsList/hooks/useRecentProfessionalsList';
import { RecentProfessionalsList } from './components/RecentProfessionalsList/RecentProfessionalsList';
import { RecommendedProsFeaturePopup } from './components/RecommendedProsFeaturePopup';
import { useRecommendedProsList } from './components/RecommendedProsList/hooks/useRecommendedProsList';
import { RecommendedProsList } from './components/RecommendedProsList/RecommendedProsList';
import { TAB_INDEX, TABS } from './utils';

const actions = [
  FETCH_FAVORITE_PROFESSIONALS,
  FETCH_RECENT_PROFESSIONALS,
  FETCH_RECOMMENDED_PROFESSIONALS_ACTION,
];

const successAddToFavoritesSelector = createSuccessSelector([ADD_FAVORITE_PROFESSIONAL]);
const successBlockProfessionalSelector = createSuccessSelector([BLOCK_RECENT_PROFESSIONAL]);

export const getInitialTab = (type) => {
  if (type === TABS.RECENT_PROFESSIONALS) return TAB_INDEX.RECENT_PROFESSIONALS;
  if (type === TABS.RECOMMENDED_PROS) return TAB_INDEX.RECOMMENDED_PROS;
  return TAB_INDEX.FAVORITES_LIST;
};

export const ProfessionalsHub = () => {
  const navType = useNavigationType();
  const params = useParams();
  const { type } = params;
  const dispatch = useDispatch();
  const user = useUserSelector();
  const isAddedToFavoritesSuccess = useSelector((state) => successAddToFavoritesSelector(state));
  const isBlockedProfessionalSuccess = useSelector((state) =>
    successBlockProfessionalSelector(state),
  );
  const { professionalHubFilter: filter, isProfessionalHubInitialized } = useAppSelector(
    (state) => state.user,
  );
  const [showSuccessAddedToFavoritesModal, setShowSuccessAddedToFavoritesModal] = useState(false);
  const [showSuccessBlockProfessionalModal, setShowSuccessBlockProfessionalModal] = useState(false);
  const [activeTab, setActiveTab] = useState(getInitialTab(type));
  const firstRenderRef = useRef(true);
  const initialDataLoadRef = useRef(false);

  const isBackNavigation = isProfessionalHubInitialized && navType === 'POP';

  const { isLoading } = useLoading(actions);

  const {
    isInitialized: isFavoritesInitialized,
    favoriteProfessionals,
    fetchFavoriteProfessionals,
  } = useFavoritesList();
  const {
    isInitialized: isRecentsInitialized,
    recentProfessionals,
    fetchRecentProfessionals,
  } = useRecentProfessionalsList();
  const {
    isInitialized: isRecommendedInitialized,
    recommendedProfessionals,
    fetchRecommendedPros,
    isRecommendedProsEnabled,
  } = useRecommendedProsList();

  const onChangeTab = useCallback(
    (tab) => {
      setActiveTab(tab);

      let selectedTab = TABS.FAVORITES_LIST;
      switch (tab) {
        case TAB_INDEX.RECENT_PROFESSIONALS:
          trackEvent('Professional Hub - "Recent" tab Clicked');
          selectedTab = TABS.RECENT_PROFESSIONALS;
          break;
        case TAB_INDEX.RECOMMENDED_PROS:
          selectedTab = TABS.RECOMMENDED_PROS;
          trackEvent('Professional Hub - "New to You" tab Clicked');
          localStorage.setItem(`isNewToYouTabNew-${user?.id}`, 'false');
          break;
        default:
          trackEvent('Professional Hub - "Favorites" tab Clicked');
          break;
      }
      history.push(`/professionals-hub/${selectedTab}`);
    },
    [user?.id],
  );

  useEffect(() => {
    dispatch(getAllPaymnets());

    // Reset filter when component unmounts (navigating away from professionals hub)
    return () => {
      dispatch(changeHubFilter('all'));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!firstRenderRef.current) {
      if (isAddedToFavoritesSuccess) {
        setShowSuccessAddedToFavoritesModal(true);
        dispatch(clearSuccess(ADD_FAVORITE_PROFESSIONAL));
      }

      if (isBlockedProfessionalSuccess) {
        setShowSuccessBlockProfessionalModal(true);
        dispatch(clearSuccess(BLOCK_RECENT_PROFESSIONAL));
      }
    } else {
      dispatch(clearSuccess([ADD_FAVORITE_PROFESSIONAL, BLOCK_RECENT_PROFESSIONAL]));
      firstRenderRef.current = false;
    }
  }, [isAddedToFavoritesSuccess, isBlockedProfessionalSuccess, dispatch]);

  useEffect(() => {
    if (isBackNavigation) {
      return;
    }

    (async () => {
      await Promise.all([
        fetchFavoriteProfessionals(),
        fetchRecentProfessionals(),
        fetchRecommendedPros(),
      ]);
    })();
  }, [filter, isAddedToFavoritesSuccess, isBlockedProfessionalSuccess]);

  useEffect(() => {
    if (
      isBackNavigation ||
      !isFavoritesInitialized ||
      !isRecentsInitialized ||
      !isRecommendedInitialized
    ) {
      return;
    }

    // Only auto-select tab on initial data load, not when filter changes
    if (!initialDataLoadRef.current) {
      initialDataLoadRef.current = true;

      if (favoriteProfessionals?.length) {
        setActiveTab(TAB_INDEX.FAVORITES_LIST);
      } else if (recentProfessionals?.length >= 3) {
        setActiveTab(TAB_INDEX.RECENT_PROFESSIONALS);
      } else if (isRecommendedProsEnabled && recommendedProfessionals?.length) {
        setActiveTab(TAB_INDEX.RECOMMENDED_PROS);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isFavoritesInitialized,
    isRecentsInitialized,
    isRecommendedInitialized,
    isRecommendedProsEnabled,
  ]);

  if (isLoading) {
    return <CardSpinner />;
  }

  return (
    <div className="component_container">
      <div
        className="floating_container"
        style={{
          flexDirection: 'column',
          justifyContent: 'flex-start',
          marginInline: '20px',
        }}
      >
        <PaymentWarningBanner containerStyle={{ marginTop: '-10px', marginInline: '-10px' }} />
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          <h1 className="global_font f-dark" style={{ alignSelf: 'center' }}>
            Professionals Hub
          </h1>

          <div
            style={{
              width: '80%',
              marginLeft: 80,
              marginTop: 20,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <FilterSelect />
            <button
              data-cy="view-invites-button"
              type="button"
              style={{
                all: 'unset',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
              }}
              onClick={() => history.push('/invites')}
            >
              <img src={MailSvg} alt="mail" style={{ width: 24, height: 24, marginLeft: 10 }} />
              <b
                style={{
                  fontFamily: 'Nunito',
                  color: Colors.secondary_500,
                  fontSize: 18,
                  cursor: 'pointer',
                }}
              >
                View Invites
              </b>
              <img src={ChevronRight} alt="arrow" style={{ width: 16, height: 16 }} />
            </button>
          </div>

          <div style={{ width: '80%', alignSelf: 'center', marginTop: 40, marginBottom: 120 }}>
            <Tabs initialTab={activeTab} onChangeTab={onChangeTab}>
              <TabList>
                <Tab
                  dataCy="favorites-tab"
                  title="Favorites"
                  totalCount={favoriteProfessionals?.length}
                />
                <Tab
                  dataCy="worked-with-tab"
                  title="Worked With"
                  totalCount={recentProfessionals?.length}
                />
                <Tab
                  dataCy="new-to-you-tab"
                  title="New to You"
                  totalCount={recommendedProfessionals?.length}
                  isNew={!localStorage.getItem(`isNewToYouTabNew-${user.id}`)}
                  isVisible={isRecommendedProsEnabled}
                />
              </TabList>
              <TabPanel>
                <FavoritesList />
                <RecentProfessionalsList />
                <RecommendedProsList />
              </TabPanel>
            </Tabs>
          </div>
        </div>
      </div>

      <RecommendedProsFeaturePopup />

      {showSuccessAddedToFavoritesModal && (
        <ConfirmPopup
          dataCy="new-favorite-added-popup"
          closePopupDataCy="new-favorite-modal-backdrop"
          title="New Favorite Added"
          description={
            <>
              This professional has been added onto your
              <br />
              favorites list tab.
            </>
          }
          descriptionStyle={{ marginBottom: 60 }}
          rightButtonText="Close"
          rightButtonAction={() => setShowSuccessAddedToFavoritesModal(false)}
          rightButtonDataCy="new-favorite-close-button"
        />
      )}

      {showSuccessBlockProfessionalModal && (
        <ConfirmPopup
          dataCy="blocked-professional-popup"
          closePopupDataCy="blocked-professional-modal-backdrop"
          title="Blocked Professional"
          description={
            <>
              This professional has been blocked and will not be able to apply to upcoming shifts.
            </>
          }
          descriptionStyle={{ marginBottom: 60 }}
          rightButtonText="Close"
          rightButtonAction={() => setShowSuccessBlockProfessionalModal(false)}
          rightButtonDataCy="close-button"
        />
      )}
    </div>
  );
};
